/**
 * SectionIndex — a small fixed index that appears once the cover has left the
 * viewport. It lives in the page gutter on very wide screens only, so it can
 * never overlap the content column. On narrower screens the floating Menu
 * button in the chrome remains the way in.
 */

import { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CHAPTERS } from "@/components/nav/chapters";
import { useAnchorNav } from "@/utils/anchorNav";
import { cn } from "@/utils/cn";

const SECTION_IDS = CHAPTERS.filter((c) => c.hash).map((c) => c.hash as string);

const SectionIndex = () => {
  const { t } = useTranslation();
  const handleAnchor = useAnchorNav();
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState<string | null>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    const cover = document.getElementById("cover-heading")?.closest("section");
    let coverObserver: IntersectionObserver | undefined;
    if (cover) {
      coverObserver = new IntersectionObserver(
        ([entry]) => setVisible(!entry.isIntersecting),
        { threshold: 0.12 },
      );
      coverObserver.observe(cover);
    }

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        const seen = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (seen?.target.id) setCurrent(seen.target.id);
      },
      { threshold: [0.15, 0.5], rootMargin: "-20% 0px -45% 0px" },
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) sectionObserver.observe(el);
    });

    return () => {
      coverObserver?.disconnect();
      sectionObserver.disconnect();
    };
  }, []);

  return (
    <nav
      aria-label={t("nav.contents")}
      aria-hidden={!visible}
      {...(!visible ? ({ inert: "" } as Record<string, string>) : {})}
      className={cn(
        "fixed left-5 top-1/2 z-40 hidden -translate-y-1/2 2xl:block",
        "transition-[opacity,transform] duration-500",
        visible ? "opacity-100" : "pointer-events-none translate-x-[-8px] opacity-0",
      )}
    >
      <ul className="space-y-3 border-l border-foreground/20 pl-4">
        {CHAPTERS.filter((c) => c.hash).map((c) => {
          const isActive = current === c.hash;
          return (
            <li key={c.key}>
              <a
                href={`/#${c.hash}`}
                onClick={handleAnchor(c.hash as string)}
                aria-current={isActive ? "true" : undefined}
                className="group flex items-center gap-3 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cobalt"
              >
                <span
                  className={cn(
                    "index-num text-xs transition-colors duration-300",
                    isActive ? "text-cobalt" : "text-muted-foreground",
                  )}
                >
                  {c.index}
                </span>
                <span
                  className={cn(
                    "h-px transition-all duration-300",
                    isActive ? "w-7 bg-cobalt" : "w-4 bg-foreground/25 group-hover:w-7",
                  )}
                  aria-hidden="true"
                />
                <span
                  className={cn(
                    "max-w-[9rem] text-[11px] font-bold uppercase leading-tight tracking-[0.12em] transition-opacity duration-300",
                    isActive
                      ? "text-foreground opacity-100"
                      : "text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
                  )}
                >
                  {t(`nav.${c.key}`)}
                </span>
              </a>
            </li>
          );
        })}
        <li className="pt-1">
          <Link
            to="/contact"
            className="flex items-center gap-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-cobalt transition-colors duration-200 hover:text-cobalt-bright focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cobalt"
          >
            <span className="index-num text-xs">04</span>
            <span className="h-px w-4 bg-cobalt/60" aria-hidden="true" />
            {t("nav.contact")}
          </Link>
        </li>
      </ul>
    </nav>
  );
};

export default memo(SectionIndex);
